import {
  ConditionalOptionValues,
  ConditionalStepField,
  CreditApplicationFields,
  CreditApplicationColumnType,
  GroupedConditionalFields,
  CreditApplicationFromFieldType,
  CheckboxOptionsOrientation,
  getOptionLabel,
} from '@dill/dill-shared';
import moment from 'moment';

export interface OptionState {
  fieldDef: ConditionalStepField;
  groupedRows: GroupedConditionalFields[];
  values: ConditionalOptionValues[];
}

// A nested field's "Show this field only if…" rule. The source answer is looked up among the
// option's own values first (same group row when grouped), then among the rest of the form.
export function isConditionalFieldVisible({
  field,
  optionValues,
  externalSourceValues,
  groupedPosition,
}: {
  field: Pick<CreditApplicationFields, 'conditionalVisibility'>;
  optionValues: ConditionalOptionValues[];
  externalSourceValues?: Record<string, unknown>;
  groupedPosition?: number | null;
}): boolean {
  if (!field.conditionalVisibility?.sourceFieldId) return true;
  const { sourceFieldId, sourceOptionValues } = field.conditionalVisibility;
  const siblings = optionValues.filter((v) => v.id === sourceFieldId);
  const sibling =
    siblings.find((v) => (v.groupedPosition ?? 0) === (groupedPosition ?? 0)) ?? siblings[0];
  const sourceValue = sibling ? sibling.value : externalSourceValues?.[sourceFieldId];
  return sourceOptionValues.includes(String(sourceValue ?? ''));
}

export function buildUniqueId({
  optionKey,
  groupName,
  groupedPosition,
  fieldId,
  fromField,
}: {
  optionKey: string;
  groupName: string | null | undefined;
  groupedPosition: number;
  fieldId: string;
  fromField?: CreditApplicationFromFieldType;
}): string {
  const keyField =
    fromField === 'DYNAMIC_FORM_DOCUMENT' ? 'dynamic-form' : 'conditional-option-select';
  return `${keyField}-${optionKey}-${groupName ?? ''}-${groupedPosition}-${fieldId}`
    .toLowerCase()
    .replace(/\s+/g, '-');
}

export function duplicateWithNextGroupedPosition(
  allFields: GroupedConditionalFields[]
): GroupedConditionalFields[] {
  const withPos = allFields.filter((f) => f.groupedPosition !== undefined);
  if (withPos.length === 0) return allFields;
  const maxPos = Math.max(...withPos.map((f) => f.groupedPosition ?? 0));
  const next = maxPos + 1;
  return [
    ...allFields,
    ...withPos
      .filter((f) => f.groupedPosition === maxPos)
      .map((row) => ({
        ...row,
        id: `${row.id.replace(/-group-\d+$/, '')}-group-${next}`,
        groupedPosition: next,
        fields: row.fields.map((f) => ({
          ...f,
          id: `${f.id.replace(/-group-\d+$/, '')}-group-${next}`,
        })),
      })),
  ];
}

export function buildValueEntry({
  optionKey,
  fieldDef,
  field,
  groupedPosition,
  index,
  groupColumnType,
  existingValues,
  savedValues,
  overrideGroupName,
  fromField,
}: {
  optionKey: string;
  fieldDef: ConditionalStepField;
  field: CreditApplicationFields;
  groupedPosition: number;
  index: number;
  groupColumnType: CreditApplicationColumnType;
  existingValues: ConditionalOptionValues[];
  savedValues: ConditionalOptionValues[];
  /** Pass "" for NOT_GROUPED fields so they save with an empty groupName and render outside group cards */
  overrideGroupName?: string;
  fromField?: CreditApplicationFromFieldType;
}): ConditionalOptionValues {
  const effectiveGroupName =
    overrideGroupName !== undefined ? overrideGroupName : fieldDef.groupName ?? '';
  const uniqueId = buildUniqueId({
    optionKey,
    groupName: effectiveGroupName,
    groupedPosition,
    fieldId: field.id,
    fromField,
  });
  const existing = existingValues.find((v) => v.uniqueId === uniqueId);
  // For ungrouped fields (overrideGroupName = ''), also try the legacy uniqueId format that used
  // fieldDef.groupName instead of '' — needed for data saved before this format change.
  const legacyUniqueId =
    overrideGroupName === '' && fieldDef.groupName
      ? buildUniqueId({
          optionKey,
          groupName: fieldDef.groupName,
          groupedPosition,
          fieldId: field.id,
          fromField,
        })
      : null;
  const saved =
    savedValues.find((v) => v.uniqueId === uniqueId) ??
    (legacyUniqueId ? savedValues.find((v) => v.uniqueId === legacyUniqueId) : undefined);
  let currentValue = existing?.value ?? saved?.value ?? '';
  if (
    field.fromField === 'DATE' &&
    field.defaultValue?.toLowerCase().trim() === 'current' &&
    (currentValue === null || currentValue === undefined || currentValue === '')
  ) {
    currentValue = moment().format('MM/DD/YYYY');
  }
  return {
    ...field,
    uniqueId,
    id: field.id,
    name: field.name,
    fromField: field.fromField,
    isRequired: field.isRequired,
    isFieldEmail: !!field.isFieldEmail,
    isHashed: !!field.isHashed,
    stepName: effectiveGroupName,
    stepNumber: fieldDef.position ?? 0,
    index,
    groupStepPosition: fieldDef.position ?? 0,
    groupName: effectiveGroupName,
    groupColumnType,
    isFieldArray: !!fieldDef.isFieldArray,
    groupedPosition,
    value: currentValue,
    optionType: field.optionType,
    secondaryText: field.secondaryText,
    secondaryValue: existing?.secondaryValue ?? saved?.secondaryValue ?? null,
    ...(fieldDef.sendEmail ? { sendEmail: fieldDef.sendEmail } : {}),
    ...(existing?.status
      ? { status: existing.status }
      : saved?.status
      ? { status: saved.status }
      : {}),
    ...(existing?.emailDate
      ? { emailDate: existing.emailDate }
      : saved?.emailDate
      ? { emailDate: saved.emailDate }
      : {}),
    ...(existing?.referenceHistoryDate
      ? { referenceHistoryDate: existing.referenceHistoryDate }
      : saved?.referenceHistoryDate
      ? { referenceHistoryDate: saved.referenceHistoryDate }
      : {}),
  };
}

export function deriveOptionState({
  optionKey,
  fieldDef,
  savedValues,
  existingValues,
  staticPreview = false,
  fromField,
}: {
  optionKey: string;
  fieldDef: ConditionalStepField;
  savedValues: ConditionalOptionValues[];
  existingValues: ConditionalOptionValues[];
  staticPreview?: boolean;
  fromField?: CreditApplicationFromFieldType;
}): OptionState {
  let groupedRows = [...(fieldDef.groupedFields ?? [])];

  if (!staticPreview && fieldDef.stepType === 'GROUPED' && groupedRows.length > 0) {
    const minGroup = fieldDef.minimunNumberOfGroups ? fieldDef.minimunNumberOfGroups - 1 : 0;
    const savedMax =
      savedValues.length > 0 ? Math.max(...savedValues.map((v) => v.groupedPosition ?? 0)) : 0;
    const requiredMax = Math.max(minGroup, savedMax);
    let currentMax = Math.max(...groupedRows.map((r) => r.groupedPosition ?? 0));
    while (currentMax < requiredMax) {
      groupedRows = duplicateWithNextGroupedPosition(groupedRows);
      currentMax++;
    }
  }

  const values: ConditionalOptionValues[] = [];

  if (groupedRows.length > 0) {
    groupedRows.forEach((row) => {
      row.fields.forEach((field) => {
        values.push(
          buildValueEntry({
            optionKey,
            fieldDef,
            field,
            groupedPosition: row.groupedPosition ?? 0,
            index: row.index ?? 0,
            groupColumnType: row.columnType ?? 'ONE_COLUMN',
            existingValues,
            savedValues,
            fromField,
          })
        );
      });
    });
  }

  if (fieldDef.unGroupedFields && fieldDef.unGroupedFields.length > 0) {
    fieldDef.unGroupedFields.forEach((field) => {
      values.push(
        buildValueEntry({
          optionKey,
          fieldDef,
          field,
          groupedPosition: 0,
          index: 0,
          groupColumnType: fieldDef.columnType ?? 'ONE_COLUMN',
          existingValues,
          savedValues,
          overrideGroupName: '',
          fromField,
        })
      );
    });
  }

  return { fieldDef, groupedRows, values };
}

// Options may carry an ERP code as "Label|code" (see splitOptionErpCode) — only the label is ever
// shown or saved; the code is looked up from the template when pushing to the ERP.
export function parseOptionList(optionList?: string | null): string[] {
  return optionList ? optionList.split(',').map(getOptionLabel) : [];
}

// DROPDOWN reuses the same `optionList` string field as OPTION_FIELD/CHECKBOX_LIST, but
// delimited by "::" instead of "," so an option's text can itself contain a comma (e.g. an address).
export function parseDropdownOptions(optionList?: string | null): string[] {
  return optionList ? optionList.split('::').map(getOptionLabel) : [];
}

export type SupplierRefOption = { name: string; erpId?: string; isHouseRep?: boolean };

type OptionSource = CreditApplicationFields['optionValueSource'];

// value and label are resolved independently: optionValueSource controls what gets *saved*
// (unique-ish, avoids ambiguity when two branches/reps share a name), optionLabelSource controls
// what's *displayed* — either can be NAME or ERP_ID regardless of the other. Falls back to the
// name whenever the chosen source's erpId is blank. When both resolve to the same string, returns
// a plain string (label === value) exactly as before — RadioInputs/CheckboxList/OptionsDropDown
// accept both shapes.
function toFieldOptions(
  records: SupplierRefOption[],
  optionValueSource?: OptionSource,
  optionLabelSource?: OptionSource
): Array<string | { label: string; value: string }> {
  const resolve = (r: SupplierRefOption, source?: OptionSource) =>
    source === 'ERP_ID' ? r.erpId?.trim() || r.name : r.name;
  return records.map((r) => {
    const value = resolve(r, optionValueSource);
    const label = resolve(r, optionLabelSource);
    return value === label ? value : { label, value };
  });
}

type SupplierRefFieldPick = Pick<
  CreditApplicationFields,
  | 'id'
  | 'name'
  | 'optionList'
  | 'optionValueSource'
  | 'optionLabelSource'
  | 'houseRepsOnly'
  | 'houseRepFieldLabel'
  | 'nonHouseRepFieldLabel'
>;

// supplier-sales-reps only: houseRepsOnly narrows the roster to reps flagged as a House Rep (see
// BranchSettings.tsx) before it's turned into options at all — applies the same way regardless of
// what optionValueSource/optionLabelSource end up showing/saving.
const filterSalesRepOptions = (
  field: SupplierRefFieldPick,
  salesRepOptions: SupplierRefOption[]
): SupplierRefOption[] =>
  field.houseRepsOnly ? salesRepOptions.filter((r) => r.isHouseRep) : salesRepOptions;

export function resolveFieldOptions(
  field: SupplierRefFieldPick,
  branchOptions: SupplierRefOption[] = [],
  salesRepOptions: SupplierRefOption[] = [],
  class1Options: string[] = []
): Array<string | { label: string; value: string }> {
  // .includes rather than === — a cloned group row's field id carries a "-group-N" suffix (see
  // DynamicStepForm.tsx/ConditionalOptionPreview.tsx's own address-sibling lookups for the same
  // issue), so an exact match silently stops recognizing these special-case ids past the first
  // group instance.
  if (field.id.includes('supplier-branches') && branchOptions.length > 0) {
    return toFieldOptions(branchOptions, field.optionValueSource, field.optionLabelSource);
  }
  if (field.id.includes('supplier-sales-reps') && salesRepOptions.length > 0) {
    return toFieldOptions(
      filterSalesRepOptions(field, salesRepOptions),
      field.optionValueSource,
      field.optionLabelSource
    );
  }
  // "Marketing Segment" (P21 Class 1) — the client wants P21's own live code list as the source
  // of truth instead of an admin-typed optionList that can go stale (see
  // getClass1CodesP21Service). Each code is both its own value and label (no name/erpId split,
  // unlike branches/sales reps), so no toFieldOptions pass is needed here.
  if (field.id.includes('marketing-segment') && class1Options.length > 0) {
    return class1Options;
  }
  return parseOptionList(field.optionList);
}

// Same "supplier-branches"/"supplier-sales-reps"/"marketing-segment" special-case as
// resolveFieldOptions, for a DROPDOWN field whose optionList is "::"-delimited instead of
// comma-delimited.
export function resolveDropdownFieldOptions(
  field: SupplierRefFieldPick,
  branchOptions: SupplierRefOption[] = [],
  salesRepOptions: SupplierRefOption[] = [],
  class1Options: string[] = []
): Array<string | { label: string; value: string }> {
  if (field.id.includes('supplier-branches') && branchOptions.length > 0) {
    return toFieldOptions(branchOptions, field.optionValueSource, field.optionLabelSource);
  }
  if (field.id.includes('supplier-sales-reps') && salesRepOptions.length > 0) {
    return toFieldOptions(
      filterSalesRepOptions(field, salesRepOptions),
      field.optionValueSource,
      field.optionLabelSource
    );
  }
  if (field.id.includes('marketing-segment') && class1Options.length > 0) {
    return class1Options;
  }
  return parseDropdownOptions(field.optionList);
}

// Picks the field's own displayed label based on whether the *currently selected* sales rep is a
// House Rep — e.g. "House Account Id" vs "Sales Rep Id" (see houseRepFieldLabel/
// nonHouseRepFieldLabel). Only applies to supplier-sales-reps fields with both configured; every
// other field (and a supplier-sales-reps field with neither set) keeps its plain field.name.
export function resolveSalesRepFieldLabel(
  field: Pick<
    CreditApplicationFields,
    'id' | 'name' | 'optionValueSource' | 'houseRepFieldLabel' | 'nonHouseRepFieldLabel'
  >,
  currentValue: unknown,
  salesRepOptions: SupplierRefOption[] = []
): string {
  if (!field.id.includes('supplier-sales-reps')) return field.name;
  if (!field.houseRepFieldLabel && !field.nonHouseRepFieldLabel) return field.name;
  if (typeof currentValue !== 'string' || !currentValue)
    return field.nonHouseRepFieldLabel || field.name;

  const selected = salesRepOptions.find((r) =>
    field.optionValueSource === 'ERP_ID'
      ? (r.erpId?.trim() || r.name) === currentValue
      : r.name === currentValue
  );
  const label = selected?.isHouseRep ? field.houseRepFieldLabel : field.nonHouseRepFieldLabel;
  return label || field.name;
}

// Flattens every sub-field definition nested under a CONDITIONAL_SELECTED_FIELD's
// conditionalOptionFields (across every option key, grouped and ungrouped) into a lookup by field
// id — a saved ConditionalOptionValues answer only carries its id/name/value, not the
// optionValueSource/houseRepsOnly that live on the definition, so resolving its display label needs
// this to look the definition back up. A sub-field's plain `id` stays stable across cloned group
// rows (only its `uniqueId` gets a "-group-N" suffix — see buildValueEntry), so one entry per id is
// enough regardless of which option key or group row it came from.
export function getConditionalSubFieldDefsById(
  field: Pick<CreditApplicationFields, 'conditionalOptionFields'>
): Map<string, SupplierRefFieldPick> {
  const byId = new Map<string, SupplierRefFieldPick>();
  Object.values(field.conditionalOptionFields ?? {}).forEach((fieldDef) => {
    if (!fieldDef) return;
    (fieldDef.unGroupedFields ?? []).forEach((f) => {
      if (!byId.has(f.id)) byId.set(f.id, f);
    });
    (fieldDef.groupedFields ?? []).forEach((row) => {
      row.fields.forEach((f) => {
        if (!byId.has(f.id)) byId.set(f.id, f);
      });
    });
  });
  return byId;
}

// Field types whose value is rendered as the full option list with the selection checked off
// (renderCheckboxOptions, helpers.ts) rather than just the bare selected string — used by both the
// builder's live template preview (PreviewInternalSignatureDocumentTemplate) and the real fill/sign-time one
// (InternalSignatureDocumentTemplatePreview.tsx), so the two stay visually identical.
export const CHECKBOX_STYLE_FIELD_TYPES = new Set([
  'OPTION_FIELD',
  'CHECKBOX_LIST',
  'CONDITIONAL_SELECTED_FIELD',
]);

// Mirrors ConditionalOptionPreview.tsx's own (inverted-sounding, but already relied on by every
// existing template's isRow flag) layout: isRow renders as a vertical stack there, not a row — see
// the `isRow ? 'flex flex-col' : 'flex flex-wrap flex-row ...'` selector. This has to match that
// exactly, orientation-for-orientation, so the read-only rendered/checked-off view (this function
// feeds renderCheckboxOptions) doesn't visually contradict the live interactive selector for the
// same field. CONDITIONAL_SELECTED_FIELD defaults to isRow: true (unlike Option Field/Checkbox
// List, which default to column) when isRow hasn't been explicitly set — see the Field Type
// dropdown in InternalSignatureDocumentsSection.tsx, which already bakes this default in when a
// field is created; the fromField check below is a fallback for wherever isRow might still arrive unset.
export function resolveCheckboxOrientation(
  field: Pick<CreditApplicationFields, 'fromField' | 'isRow'>
): CheckboxOptionsOrientation {
  if (field.isRow === false) return 'row';
  if (field.isRow || field.fromField === 'CONDITIONAL_SELECTED_FIELD') return 'column';
  return 'row';
}

export function groupRowsByPosition(
  rows: GroupedConditionalFields[]
): Record<number, GroupedConditionalFields[]> {
  return rows.reduce<Record<number, GroupedConditionalFields[]>>((acc, row) => {
    const pos = row.groupedPosition ?? 0;
    (acc[pos] ??= []).push(row);
    return acc;
  }, {});
}
